<template>
  <div
    class="min-h-[stretch] tablet-h:min-h-screen max-h-[stretch] tablet-h:max-h-screen bg-[#FAFAFA] overflow-hidden"
  >
    <div
      class="flex flex-col px-3 py-5 tablet-v:px-4 tablet-h:px-5 w-full overflow-y-auto"
      :style="containerStyles"
    >
      <div class="w-full max-w-[1342px] mx-auto">
        <NewLink type="breadcrumb" class="mb-5" @click="router.push(fileRoute)">
          Copyright
        </NewLink>

        <!-- Header skeleton -->
        <div
          v-if="loading"
          class="w-full min-h-[120px] px-[15px] tablet-h:px-[30px] py-5 border border-[#CCC]/50 rounded-xl flex tablet-h:items-center gap-x-5 mb-[18px]"
        >
          <Skeleton class="min-w-[80px] h-[80px] rounded-md" />
          <div class="flex-1">
            <Skeleton class="w-[200px] h-[26px] mb-3" />
            <div class="flex gap-x-10">
              <div>
                <Skeleton class="w-[120px] h-[18px] mb-1.5" />
                <Skeleton class="w-[160px] h-[18px]" />
              </div>
              <div>
                <Skeleton class="w-[100px] h-[18px] mb-1.5" />
                <Skeleton class="w-[140px] h-[18px]" />
              </div>
            </div>
          </div>
        </div>

        <!-- Header -->
        <div
          v-else
          class="w-full min-h-[120px] px-[15px] tablet-h:px-[30px] py-5 border border-[#CCC]/50 rounded-xl flex tablet-h:items-center gap-x-5 mb-[18px]"
        >
          <div
            class="min-w-[80px] h-[80px] border border-[#CCC]/50 rounded-md bg-[#FAFAFA] flex items-center justify-center"
          >
            <ThumbnailIcon
              class="w-[46px] h-[46px]"
              :type="file?.type"
              :icon-size="46"
              :image-url="applyAuthorizationToken(file?.asset.previewImageUrl)"
              showShadow
              transition
            />
          </div>

          <div>
            <div class="flex mb-3 gap-x-5">
              <div
                class="font-primary font-medium text-[17px] leading-[26px] text-gray-800"
              >
                {{ file?.name }}
              </div>

              <RouterLink
                :to="fileRoute"
                class="w-fit! tablet-h:hidden self-start ml-auto"
              >
                <UiButton>View</UiButton>
              </RouterLink>
            </div>

            <div
              class="flex flex-col tablet-h:flex-row tablet-h:items-center gap-x-10 gap-y-4"
            >
              <div>
                <div
                  class="font-primary font-medium text-[13px] leading-[18px] text-gray-800 flex items-center mb-1.5"
                >
                  <Icon :size="16" name="Dacr" class="fill-gray-800 mr-1" />
                  <div>Dacr Timestamp</div>
                </div>

                <div
                  class="font-secondary text-[14px] leading-[18px] text-gray-800 flex items-center"
                >
                  {{
                    formatTimestamp(file?.copyright.registrations[0]?.timestamp)
                  }}
                </div>
              </div>

              <div>
                <div
                  class="font-primary font-medium text-[13px] leading-[18px] text-gray-800 flex items-center mb-1.5"
                >
                  <div>Dacr Token ID</div>
                </div>

                <div
                  class="font-secondary text-[14px] leading-[18px] text-gray-800 flex items-center break-all"
                >
                  {{ file?.copyright.id }}
                </div>
              </div>
            </div>
          </div>

          <RouterLink
            :to="fileRoute"
            class="hidden tablet-h:block self-start ml-auto"
          >
            <UiButton>View registration</UiButton>
          </RouterLink>
        </div>

        <Card.Base
          class="mb-4 border-2 border-transparent border--glow relative"
        >
          <Card.Title icon="File">
            <div>Sheet music</div>

            <div
              class="text-[12px] leading-[15px] bg-gradient-to-r from-[#30BAC6]/20 from-60% to-[#69B393]/20 absolute top-0 right-0 rounded-tr-[10px] rounded-bl-xl h-7 text-gray-900 flex items-center px-3"
            >
              <img :src="Fingerprint" class="w-[14px] mr-2" />
              Registered for protection
            </div>
          </Card.Title>

          <Card.Content>
            <div class="mb-[22px]">
              Analyzed with
              <span
                class="w-fit bg-linear-to-r from-yellow-500 via-[#F84600] to-[#CE489A] bg-clip-text font-medium text-transparent"
              >
                Dacr AI
              </span>
            </div>

            <!-- Content skeleton -->
            <div v-if="sheetLoading" class="flex flex-col gap-y-4">
              <Skeleton class="w-full h-[60px]" />
              <Skeleton class="w-full h-[60px]" />
              <Skeleton class="w-full h-[60px]" />
              <Skeleton class="w-[70%] h-[60px]" />
            </div>

            <!-- Content -->
            <div ref="sheet"></div>
          </Card.Content>
        </Card.Base>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import NewLink from '@/shared/components/NewLink.vue'
import { useCopyrightFile } from '@/features/copyright/composables/useCopyrightFile'
import { useAvailableHeight } from '@/shared/composables/useAvailableHeight'
import { formatTimestamp } from '@/shared/utils/datetime'
import { applyAuthorizationToken, downloadURLContent } from '@/shared/utils/url'
import { Card, Icon, Skeleton, ThumbnailIcon, UiButton } from '@dacr/ui'
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Fingerprint from '@/assets/images/Fingerprint.png'
import { OpenSheetMusicDisplay } from 'opensheetmusicdisplay'

const { availableHeight } = useAvailableHeight()
const route = useRoute()
const router = useRouter()

const sheet = ref<HTMLElement>()

const fileId = computed(() => route.params.id as string)
const trackIndex = computed(() => Number(route.params.trackIndex))
const stemName = computed(() => route.params.stem as string)

const { file, loading } = useCopyrightFile(fileId)

const sheetMusicUrl = computed(() => {
  const analysis = file.value?.analysis

  if (!analysis) return null

  if (
    analysis.__typename === 'AudioAnalysis' ||
    analysis.__typename === 'VideoAnalysis'
  ) {
    const track = analysis.tracks[trackIndex.value]
    const stem = track.stems.breakdown.find(
      (stem) => stem.name.toLowerCase() === stemName.value
    )

    return stem?.sheetMusic?.url
  }

  return null
})

const sheetLoading = ref(true)

watch([sheetMusicUrl, sheet], async ([url, sheet]) => {
  if (!url || !sheet) {
    return
  }

  try {
    const osmd = new OpenSheetMusicDisplay(sheet)
    osmd.setOptions({
      drawTitle: false,
      drawPartNames: false,
      drawCredits: false,
      drawingParameters: 'compact',
    })
    await osmd.load(url)
    osmd.render()
  } finally {
    sheetLoading.value = false
  }
})

const containerStyles = computed(() => ({
  height: `${availableHeight.value}px`,
}))

const fileRoute = computed(() => `/file/${route.params.id}`)
</script>

<style scoped>
:global(#sheet svg) {
  width: 100% !important;
  height: 100% !important;
}
</style>
